import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { DomainEventBus, DomainEventType } from '../events/domain-event-bus';
import { metrics, trace } from '@opentelemetry/api';
import { flushMetrics } from '../../../opentelemetry/init-otel';
import { OTEL_SERVICE_NAME } from './telemetry.constants';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';

@Injectable({ providedIn: 'root' })
export class LoaderTelemetryService {
  private meter = metrics.getMeter(OTEL_SERVICE_NAME);
  private tracer = trace.getTracer(OTEL_SERVICE_NAME);
  private stuckCounter = this.meter.createCounter('ui_loader_stuck_total', { description: 'Loader visible beyond threshold' });
  private logger = logs.getLogger(OTEL_SERVICE_NAME);
  private visibleSince: number | null = null;
  private stuckTimer: number | null = null;
  private stuckReported = false;
  private lastSessionId: string | undefined;
  private readonly STUCK_MS = 15000;

  constructor(bus: DomainEventBus) {
    bus.events$.pipe(filter((e) => e.type === DomainEventType.LoaderVisible)).subscribe((e: any) => {
      this.lastSessionId = e.sessionId;
      if (e.visible) {
        if (this.visibleSince == null) this.visibleSince = e.timestamp;
        this.stuckReported = false;
        if (this.stuckTimer) clearTimeout(this.stuckTimer);
        this.stuckTimer = window.setTimeout(() => this.reportIfStuck(), this.STUCK_MS);
      } else {
        this.visibleSince = null;
        if (this.stuckTimer) {
          clearTimeout(this.stuckTimer);
          this.stuckTimer = null;
        }
        this.stuckReported = false;
        void flushMetrics();
      }
    });

    window.addEventListener('beforeunload', () => {
      this.reportIfStuck();
    });
  }

  private reportIfStuck() {
    if (this.visibleSince == null || this.stuckReported) return;
    const visibleMs = Date.now() - this.visibleSince;
    if (visibleMs >= this.STUCK_MS) {
      this.emitStuck(visibleMs);
      this.stuckReported = true;
    }
  }

  private emitStuck(visibleMs: number) {
    try {
      this.stuckCounter.add(1, { path: window.location.pathname, threshold_ms: this.STUCK_MS, visible_ms: visibleMs });

      this.tracer.startActiveSpan('ui.loader.stuck', (span) => {
        try {
          span.setAttribute('session.id', this.lastSessionId || '');
        } catch {}
        span.setAttribute('path', window.location.pathname);
        span.setAttribute('threshold_ms', this.STUCK_MS);
        span.setAttribute('visible_ms', visibleMs);

        const spanContext = span.spanContext();
        const traceId = spanContext.traceId;
        const spanId = spanContext.spanId;

        this.logger.emit({
          body: 'UI loader stuck',
          severityNumber: SeverityNumber.WARN,
          severityText: 'WARN',
          attributes: {
            'session.id': this.lastSessionId,
            path: window.location.pathname,
            threshold_ms: this.STUCK_MS,
            visible_ms: visibleMs,
            'trace.id': traceId,
            'span.id': spanId
          }
        });

        span.end();
      });

      void flushMetrics();
    } catch {}
  }
}
